// What the HUD shows, as data.
//
// Every judgement the HUD makes — which single line the top strip carries, what
// the shopkeeper just said, whether the buy prompt is affordable — lives here
// as a pure function of a snapshot, so it can be read and tested without a DOM
// and without a canvas. The components below it only place the answer.

import { CHAT_HISTORY_LIMIT, POTION_COST } from "../../sim/constants";
import type { ChatLineWire, ShopResult, Snapshot } from "../../shared/protocol";

/** How many chat lines the log shows. Kept small: the log sits over the world,
 *  and a panel that covers half the field to show four empty rows is worse than
 *  a short one. */
export const CHAT_LINES = 4;

/** HUD text is the old canvas HUD's seven logical pixels at the picture's own
 *  upscale, held between the size it stops being readable at and the size it
 *  stops being a HUD at. The loop sets it as a custom property and every
 *  measurement in the overlay is an `em` off it, so one number sizes the lot. */
export const hudFontPx = (scale: number): number => Math.min(26, Math.max(12, Math.round(scale * 7)));

export interface HudModel {
  /** Six characters, and the only thing that tells two players apart. */
  slug: string;
  /** Whether the socket is up. A dropped one is silent otherwise: the world
   *  simply stops answering and the player keeps pressing keys. */
  connected: boolean;
  /** True until the first snapshot with a body in it. */
  entering: boolean;
  hp: number;
  maxHp: number;
  gold: number;
  online: number;
  atShop: boolean;
  /** Whole seconds until the body is back up, 0 while it is alive. */
  respawn: number;
  /** The last thing the shopkeeper said, or "" once it has timed out. */
  note: string;
  chat: readonly ChatLineWire[];
  /** Whether the chat line has the keyboard. */
  typing: boolean;
  /** The maintenance message while the game is locked, "" while it is open. */
  maintenance: string;
  /** Whether to show the on-screen controls: a touch screen is the primary
   *  pointer, or the player has touched the picture. */
  touch: boolean;
}

export const emptyHud = (slug = ""): HudModel => ({
  slug,
  connected: false,
  entering: true,
  hp: 0,
  maxHp: 1,
  gold: 0,
  online: 0,
  atShop: false,
  respawn: 0,
  note: "",
  chat: [],
  typing: false,
  maintenance: "",
  touch: false,
});

/** The half of the model a snapshot decides.
 *
 *  `respawn` is rounded here rather than at the point it is drawn, so the store
 *  sees one change a second instead of one a frame — a countdown that ticks
 *  sixty times a second is sixty DOM writes for one visible digit. */
export function vitalsFrom(snap: Snapshot): Pick<HudModel, "hp" | "maxHp" | "gold" | "online" | "atShop" | "respawn"> {
  return {
    hp: Math.max(0, snap.self.hp),
    maxHp: snap.self.maxHp,
    gold: snap.self.gold,
    online: snap.online,
    atShop: snap.self.atShop,
    respawn: Math.ceil(Math.max(0, snap.self.respawn)),
  };
}

/** The tone the prompt is said in. These are class names, not colours: the
 *  palette lives in the stylesheet now. */
export type PromptTone = "plain" | "dim" | "warn" | "gold";

export interface Prompt {
  text: string;
  tone: PromptTone;
  /** What a screen reader is told. It is not always the visible text: the death
   *  line counts down once a second, and announcing "back in 4, back in 3" is
   *  how a live region becomes something people switch off. */
  said: string;
}

/** One centred line of instruction, or nothing.
 *
 *  It sits along the top edge rather than above the chat log, because the log
 *  grows when somebody starts typing and a prompt that moves out from under a
 *  panel is a prompt you read twice. The top strip is the one part of the HUD
 *  with nothing else in the middle of it. */
export function promptFor(model: HudModel): Prompt | null {
  if (!model.connected) return { text: "reconnecting…", tone: "dim", said: "reconnecting" };
  // The maintenance panel says it in full; the strip stays empty under it.
  if (model.maintenance) return null;
  if (model.respawn > 0) {
    return { text: `you died. back in ${model.respawn}`, tone: "warn", said: "you died" };
  }
  if (model.note) return { text: model.note, tone: "plain", said: model.note };
  // Offering a purchase the server will refuse is a lie the HUD does not need
  // to tell: a potion is drunk on the spot, so a full body cannot use one and
  // the keeper says so.
  if (model.atShop && model.hp >= model.maxHp) {
    const text = "the shopkeeper has nothing you need";
    return { text, tone: "dim", said: text };
  }
  if (model.atShop) {
    const text = model.touch ? `buy a potion (${POTION_COST}g)` : `press E to buy a potion (${POTION_COST}g)`;
    return { text, tone: model.gold >= POTION_COST ? "gold" : "dim", said: text };
  }
  return null;
}

/** What the keeper says back. */
export function shopReply(result: ShopResult): string {
  switch (result) {
    case "bought":
      return "you drink the potion";
    case "too-poor":
      return `you cannot afford ${POTION_COST}g`;
    case "already-full":
      return "you are already well";
    case "too-far":
      return "there is nobody here to buy from";
  }
}

/** Append one line and drop the oldest once the scrollback is full — the same
 *  bound the world keeps, read from the same constant. A new array every time,
 *  because the store compares by reference. */
export function withChatLine(chat: readonly ChatLineWire[], line: ChatLineWire): readonly ChatLineWire[] {
  const next = [...chat, line];
  return next.length > CHAT_HISTORY_LIMIT ? next.slice(next.length - CHAT_HISTORY_LIMIT) : next;
}
